import React, { useEffect, useMemo, useState } from 'react';
import { FaHospital, FaPlus, FaTimes } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

type TenantClient = {
  id: string;
  name: string;
  organization_id?: string;
};

type TenantFacility = {
  id: string;
  name: string;
  clientId?: string;
  code?: string;
};

const normalize = (value?: string) => (value || '').trim().toLowerCase();

const ClientFacilitiesAdmin: React.FC = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState<TenantClient[]>([]);
  const [facilities, setFacilities] = useState<TenantFacility[]>([]);
  const [selectedClient, setSelectedClient] = useState('all');

  // Create modal state
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState({ name: '', code: '', client_slug: '' });

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiCall('/core/tenant-context/', { method: 'GET' }, token);
      if (!response.ok) throw new Error(`Failed to load facilities: HTTP ${response.status}`);
      const data = await response.json();

      const loadedClients: TenantClient[] = Array.isArray(data.clients)
        ? data.clients.map((client: any) => ({
            id: String(client.id || ''),
            name: String(client.name || client.id || ''),
            organization_id: String(client.organization_id || ''),
          }))
        : [];

      const loadedFacilities: TenantFacility[] = Array.isArray(data.facilities)
        ? data.facilities.map((facility: any) => ({
            id: String(facility.id || ''),
            name: String(facility.name || ''),
            clientId: String(facility.clientId || ''),
            code: String(facility.code || ''),
          }))
        : [];

      setClients(loadedClients);
      setFacilities(loadedFacilities);
    } catch (e: any) {
      setError(e.message || 'Failed to load facilities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [token]);

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((client) => map.set(normalize(client.id), client.name));
    return map;
  }, [clients]);

  const filteredFacilities = useMemo(() => {
    const selected = normalize(selectedClient);
    const rows = facilities.filter((facility) => {
      if (selected === 'all') return true;
      return normalize(facility.clientId) === selected;
    });
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [facilities, selectedClient]);

  const openModal = () => {
    setForm({ name: '', code: '', client_slug: selectedClient !== 'all' ? selectedClient : '' });
    setSaveError('');
    setShowModal(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { setSaveError('Facility name is required.'); return; }
    if (!form.client_slug.trim()) { setSaveError('Client is required.'); return; }
    setSaving(true);
    setSaveError('');
    try {
      const response = await apiCall('/core/admin/facilities/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), code: form.code.trim(), client_slug: form.client_slug }),
      }, token);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      setShowModal(false);
      await loadData();
    } catch (e: any) {
      setSaveError(e.message || 'Failed to create facility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-base-content flex items-center gap-3">
          <FaHospital className="text-primary" />
          Facilities
        </h1>
        <button className="btn btn-primary" onClick={openModal}>
          <FaPlus className="mr-2" /> Add Facility
        </button>
      </div>

      <div className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body space-y-4">
          <label className="form-control w-full md:w-80">
            <span className="label">
              <span className="label-text font-semibold">Client</span>
            </span>
            <select
              className="select select-bordered"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
            >
              <option value="all">All</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </label>

          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="table table-zebra table-fixed w-full">
              <thead>
                <tr>
                  <th className="w-[36%]">Facility</th>
                  <th className="w-[36%]">Client</th>
                  <th className="w-[28%]">Code</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="text-center">Loading facilities...</td></tr>
                ) : filteredFacilities.length === 0 ? (
                  <tr><td colSpan={3} className="text-center">No facilities found.</td></tr>
                ) : (
                  filteredFacilities.map((facility) => (
                    <tr key={facility.id}>
                      <td>{facility.name}</td>
                      <td>{clientNameById.get(normalize(facility.clientId)) || facility.clientId || '-'}</td>
                      <td>{facility.code || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Facility Modal */}
      {showModal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Add New Facility</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="space-y-4">
              <div className="form-control">
                <label className="label"><span className="label-text font-semibold">Client *</span></label>
                <select
                  className="select select-bordered w-full"
                  value={form.client_slug}
                  onChange={(e) => setForm({ ...form, client_slug: e.target.value })}
                >
                  <option value="">Select a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-control">
                <label className="label"><span className="label-text font-semibold">Facility Name *</span></label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="e.g. City South Surgery"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Short Code</span>
                  <span className="label-text-alt text-base-content/50">Optional — used in scheduler labels</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full uppercase"
                  placeholder="e.g. CSS"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                />
              </div>

              {saveError && (
                <div className="alert alert-error">
                  <span>{saveError}</span>
                </div>
              )}
            </div>

            <div className="modal-action">
              <button className="btn" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? <span className="loading loading-spinner loading-sm" /> : 'Add Facility'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientFacilitiesAdmin;
